/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

/**
 *
 * Rebuilds file content to exactly reflect the drag-reordered
 * HeTreeNode tree, including field-level moves across blocks.
 *
 */
import { FileType } from "../../Api/Api";
import {
  PROTO_MODIFIERS,
  PROTO_PRIMITIVE_TYPES,
  PROTO_STRUCTURES,
  PROTO_TOKENS,
  PROTO_XML_FIELD_TAGS,
  XML_TOKENS,
} from "../../Utils/Common";
import { HeTreeNode } from "./ComposerNodeHelpers";

export function serializeTreeToContent(
  treeData: HeTreeNode[],
  fileType: FileType,
  originalContent: string
): string {
  if (!originalContent) return originalContent;
  try {
    if (fileType === FileType.XML || isXmlLike(originalContent)) {
      return reorderXmlContent(treeData, originalContent);
    }
    if (fileType === FileType.PROTO || isProtoLike(originalContent)) {
      return reorderProtoContent(treeData, originalContent);
    }
    return originalContent;
  } catch (e) {
    return originalContent;
  }
}

// ─── Format detection ─────────────────────────────────────────────────────────

function isXmlLike(s: string): boolean {
  return s.trimStart().startsWith(XML_TOKENS.XML_DECLARATION);
}

function isProtoLike(s: string): boolean {
  return PROTO_TOKENS.SYNTAX_DECLARATION.test(s);
}

// ─── Protobuf XML tags ────────────────────────────────────────────────────────
const [MODIFIER, TYPE, NAME, NUMBER] = PROTO_XML_FIELD_TAGS;

// ─── Proto structure keywords ─────────────────────────────────────────────────
const [MESSAGE, ENUM, SERVICE, ONEOF, RPC, MAP] = PROTO_STRUCTURES;

// Proto structures grouped by how they're delimited in source:
//   block — brace-delimited bodies (`message Foo { ... }`)
//   rpc   — paren-signature statements (`rpc Foo(Req) returns (Res);`)
//   map   — generic field type (`map<K, V> name = N;`)
const PROTO_STRUCTURE_KEYWORDS = {
  block: [MESSAGE, ENUM, SERVICE, ONEOF],
  rpc: [RPC],
  map: [MAP],
  get all() {
    return [...this.block, ...this.rpc, ...this.map];
  },
} as const;

const PROTO_BLOCK_KEYWORDS = PROTO_STRUCTURE_KEYWORDS.block;
const PROTO_RPC_KEYWORDS = PROTO_STRUCTURE_KEYWORDS.rpc;
const PROTO_MAP_KEYWORDS = PROTO_STRUCTURE_KEYWORDS.map;

// ─── Shared label → key mapping ───────────────────────────────────────────────

type NodeKeyInfo =
  | { kind: "block"; key: string; syntheticTag: string | null }
  | { kind: "field"; key: string }
  | null;

const PROTO_BLOCK_KEYWORD_PATTERN = new RegExp(
  `^<(${PROTO_BLOCK_KEYWORDS.join("|")})\\s+(\\S+?)>$`,
  "i"
);

const PROTO_RPC_KEYWORD_PATTERN = new RegExp(
  `^<(${PROTO_RPC_KEYWORDS.join("|")})\\s+(\\S+?)>$`,
  "i"
);

function labelToKeyInfo(label: string): NodeKeyInfo {
  const trimmed = label.trim();

  // ── XML-bracket labels (xml→proto tree nodes) ──────────────────────────────
  // Block label:  <message Person>  |  <enum Status>  |  <service Foo>
  const xmlBlockLabel = trimmed.match(PROTO_BLOCK_KEYWORD_PATTERN);
  if (xmlBlockLabel)
    return {
      kind: "block",
      key: xmlBlockLabel[2].toLowerCase(),
      syntheticTag: xmlBlockLabel[2],
    };

  // RPC block label:  <rpc SayHello>
  const xmlRpcLabel = trimmed.match(PROTO_RPC_KEYWORD_PATTERN);
  if (xmlRpcLabel)
    return {
      kind: "block",
      key: `rpc:${xmlRpcLabel[2]}`.toLowerCase(),
      syntheticTag: null,
    };

  // Field label:  <Person int32 id>  |  <Person string name>
  // The field name is always the last whitespace-delimited token before `>`.
  // RPC bracket label:  <UserService rpc GetUser>
  // Checked before xmlFieldLabel — the catch-all would swallow it first.
  const PROTO_XML_RPC_BRACKET_PATTERN = new RegExp(
    `^<\\S+\\s+(?:${PROTO_RPC_KEYWORDS.join("|")})\\s+(\\S+?)>$`,
    "i"
  );
  const xmlRpcBracketLabel = trimmed.match(PROTO_XML_RPC_BRACKET_PATTERN);
  if (xmlRpcBracketLabel)
    return {
      kind: "block",
      key: `rpc:${xmlRpcBracketLabel[1]}`.toLowerCase(),
      syntheticTag: null,
    };

  const PROTO_XML_ONEOF_BRACKET_PATTERN = new RegExp(
    `^<\\S+\\s+(?:${ONEOF})\\s+(\\S+?)>$`,
    "i"
  );
  const xmlOneofBracketLabel = trimmed.match(PROTO_XML_ONEOF_BRACKET_PATTERN);
  if (xmlOneofBracketLabel)
    return {
      kind: "block",
      key: xmlOneofBracketLabel[1].toLowerCase(),
      syntheticTag: null,
    };

  const xmlFieldLabel = trimmed.match(/^<\S.*\s+(\S+?)>$/);
  if (xmlFieldLabel)
    return { kind: "field", key: xmlFieldLabel[1].toLowerCase() };
  // ──────────────────────────────────────────────────────────────────────────

  const oneofMatch = trimmed.match(new RegExp(`^${ONEOF}\\s+(\\S+)`));
  if (oneofMatch)
    return {
      kind: "block",
      key: oneofMatch[1].toLowerCase(),
      syntheticTag: null,
    };

  const mapMatch = trimmed.match(
    new RegExp(`^(?:${PROTO_MAP_KEYWORDS.join("|")})<[^>]+>\\s+(\\S+)`, "i")
  );
  if (mapMatch)
    return {
      kind: "block",
      key: mapMatch[1].toLowerCase(),
      syntheticTag: null,
    };

  const PROTO_RPC_MATCH_PATTERN = new RegExp(
    `(?:^|\\s)(${PROTO_RPC_KEYWORDS.join("|")})\\s+(\\S+)`
  );
  const rpcMatch = trimmed.match(PROTO_RPC_MATCH_PATTERN);
  if (rpcMatch)
    return {
      kind: "block",
      key: `${rpcMatch[1]}:${rpcMatch[2]}`.toLowerCase(),
      syntheticTag: null,
    };

  const PROTO_BLOCK_MATCH_PATTERN = new RegExp(
    `^(${PROTO_BLOCK_KEYWORDS.join("|")})\\s+(\\S+)`,
    "i"
  );
  const blockMatch = trimmed.match(PROTO_BLOCK_MATCH_PATTERN);
  if (blockMatch)
    return {
      kind: "block",
      key: blockMatch[2].toLowerCase(),
      syntheticTag: blockMatch[2],
    };

  const PROTO_MODIFIER_PATTERN = new RegExp(
    `^(?:${PROTO_MODIFIERS.join("|")})\\s+\\S+\\s+(\\S+)$`
  );

  const modifierFieldMatch = trimmed.match(PROTO_MODIFIER_PATTERN);
  if (modifierFieldMatch)
    return { kind: "field", key: modifierFieldMatch[1].toLowerCase() };

  const PROTO_PRIMITIVE_TYPE_PATTERN = new RegExp(
    `^(?:${PROTO_PRIMITIVE_TYPES.join("|")})\\s+(\\S+)$`
  );

  const primitiveTypeFieldMatch = trimmed.match(PROTO_PRIMITIVE_TYPE_PATTERN);
  if (primitiveTypeFieldMatch)
    return { kind: "field", key: primitiveTypeFieldMatch[1].toLowerCase() };

  const messageTypeFieldMatch = trimmed.match(/^[A-Z]\S*\s+(\S+)$/);
  if (messageTypeFieldMatch)
    return { kind: "field", key: messageTypeFieldMatch[1].toLowerCase() };

  if (/^[A-Z_][A-Z0-9_]*\s*=\s*\d+$/.test(trimmed)) {
    const name = trimmed.split("=")[0].trim();
    return { kind: "field", key: name.toLowerCase() };
  }

  const rawOneofMatch = trimmed.match(/^(\S+)\s+oneof\s+(\S+)$/i);
  if (rawOneofMatch)
    return {
      kind: "block",
      key: rawOneofMatch[2].toLowerCase(),
      syntheticTag: null,
    };

  const rawPathTokens = trimmed.split(/\s+/);
  const PROTO_ALL_BLOCK_KEYWORDS_PATTERN = new RegExp(
    `^(${PROTO_STRUCTURE_KEYWORDS.all.join("|")})$`,
    "i"
  );
  if (
    rawPathTokens.length >= 2 &&
    !PROTO_ALL_BLOCK_KEYWORDS_PATTERN.test(rawPathTokens[0])
  ) {
    return {
      kind: "field",
      key: rawPathTokens[rawPathTokens.length - 1].toLowerCase(),
    };
  }

  return null;
}

const INDENT = "  ";

// ═════════════════════════════════════════════════════════════════════════════
// XML
// ═════════════════════════════════════════════════════════════════════════════

type ItemKind = "field" | "block" | "opaque";

interface ContentItem {
  kind: ItemKind;
  key: string | null;
  rawLines?: string[];
  rawLine?: string;
  openTag?: string;
  closeTag?: string;
  openLine?: string;
  closeLine?: string;
  childItems?: ContentItem[];
}

interface XmlParsedDocument {
  blocks: Map<string, ContentItem>;
  preamble: string;
  epilogue: string;
}

// ─── XML parsing ──────────────────────────────────────────────────────────────

function extractXmlTagName(line: string): string | null {
  const match = line.trim().match(/^<([A-Za-z0-9_:.-]+)/);
  return match ? match[1] : null;
}

/**
 * XML protobuf tags namespace structural nodes with a structure prefix, e.g.
 * `<oneof:content_oneof>`, `<Map:resource_map>`, `<Rpc:SendMessage>`. The tree
 * labels, however, resolve to BARE keys ("content_oneof", "resource_map") via
 * labelToKeyInfo. Strip a leading `prefix:` so a bare tree key can be aliased to
 * a prefixed block tag during registration. Returns null when there's no prefix.
 */
function stripXmlNamespacePrefix(key: string): string | null {
  const colonIndex = key.indexOf(":");
  if (colonIndex === -1) return null;
  const bare = key.slice(colonIndex + 1);
  return bare.length > 0 ? bare : null;
}

function isInlineLine(line: string): boolean {
  const trimmed = line.trim();
  if (
    !trimmed.startsWith(XML_TOKENS.OPEN_TAG) ||
    trimmed.startsWith(XML_TOKENS.CLOSE_TAG)
  )
    return false;
  const opens = (trimmed.match(/<(?![/?!])/g) || []).length;
  const closes = (trimmed.match(/<\//g) || []).length;
  return opens === closes && opens > 0;
}

function parseXmlBlockContents(lines: string[]): ContentItem[] {
  const items: ContentItem[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
      continue;
    }

    const tag = extractXmlTagName(trimmed)?.toLowerCase();
    if (!tag) {
      i++;
      continue;
    }

    if (tag === MODIFIER || tag === TYPE) {
      const cluster = tryParseXmlFieldCluster(lines, i);
      if (cluster) {
        items.push(cluster.item);
        i = cluster.nextIndex;
        continue;
      }
    }

    if (isInlineLine(line)) {
      items.push({ kind: "opaque", key: null, rawLines: [line] });
      i++;
      continue;
    }

    if (
      trimmed.startsWith(XML_TOKENS.OPEN_TAG) &&
      !trimmed.startsWith(XML_TOKENS.CLOSE_TAG) &&
      !trimmed.endsWith(XML_TOKENS.SELF_CLOSE)
    ) {
      const block = consumeXmlBlock(lines, i);
      if (block) {
        const innerLines = block.rawLines.slice(1, block.rawLines.length - 1);
        const childItems = parseXmlBlockContents(innerLines);
        items.push({
          kind: "block",
          key: block.tagName.toLowerCase(),
          rawLines: block.rawLines,
          openTag: block.rawLines[0],
          closeTag: block.rawLines[block.rawLines.length - 1],
          childItems,
        });
        i = block.nextIndex;
        continue;
      }
    }

    items.push({ kind: "opaque", key: null, rawLines: [line] });
    i++;
  }

  return items;
}

function consumeXmlBlock(
  lines: string[],
  startIndex: number
): { tagName: string; rawLines: string[]; nextIndex: number } | null {
  const firstLine = lines[startIndex];
  const tagName = extractXmlTagName(firstLine.trim());
  if (!tagName) return null;

  const rawLines: string[] = [firstLine];
  let nestingDepth = 1;
  let i = startIndex + 1;

  while (i < lines.length && nestingDepth > 0) {
    const line = lines[i];
    rawLines.push(line);
    const trimmed = line.trim();
    const opens = (trimmed.match(/<(?![/?!])/g) || []).length;
    const closes = (trimmed.match(/<\//g) || []).length;
    const selfClose = (trimmed.match(/\/>/g) || []).length;
    nestingDepth += opens - selfClose - closes;
    i++;
  }

  return { tagName, rawLines, nextIndex: i };
}

function tryParseXmlFieldCluster(
  lines: string[],
  startIndex: number
): { item: ContentItem; nextIndex: number } | null {
  const clusterLines: string[] = [];
  let i = startIndex;
  let fieldName: string | null = null;

  if (
    i < lines.length &&
    extractXmlTagName(lines[i].trim())?.toLowerCase() === MODIFIER &&
    isInlineLine(lines[i])
  ) {
    clusterLines.push(lines[i]);
    i++;
  }

  if (
    i < lines.length &&
    extractXmlTagName(lines[i].trim())?.toLowerCase() === TYPE &&
    isInlineLine(lines[i])
  ) {
    clusterLines.push(lines[i]);
    i++;
  } else {
    return null;
  }

  if (
    i < lines.length &&
    extractXmlTagName(lines[i].trim())?.toLowerCase() === NAME &&
    isInlineLine(lines[i])
  ) {
    clusterLines.push(lines[i]);
    const nameMatch = lines[i].trim().match(/^<name>([^<]*)<\/name>/);
    if (nameMatch) fieldName = nameMatch[1].trim();
    i++;
  } else {
    return null;
  }

  if (
    i < lines.length &&
    extractXmlTagName(lines[i].trim())?.toLowerCase() === NUMBER &&
    isInlineLine(lines[i])
  ) {
    clusterLines.push(lines[i]);
    i++;
  } else {
    return null;
  }

  if (!fieldName || clusterLines.length < 3) return null;

  return {
    item: {
      kind: "field",
      key: fieldName.toLowerCase(),
      rawLines: clusterLines,
    },
    nextIndex: i,
  };
}

function buildXmlParsedDocument(content: string): XmlParsedDocument {
  const lines = content.split("\n");

  let preambleEnd = 0;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (
      trimmed === "" ||
      trimmed.startsWith(XML_TOKENS.PROCESSING_INSTRUCTION) ||
      trimmed.startsWith(XML_TOKENS.COMMENT)
    ) {
      preambleEnd = i + 1;
      continue;
    }
    if (
      trimmed.startsWith(XML_TOKENS.OPEN_TAG) &&
      !trimmed.startsWith(XML_TOKENS.CLOSE_TAG) &&
      !trimmed.startsWith(XML_TOKENS.PROCESSING_INSTRUCTION)
    ) {
      preambleEnd = i + 1;
      break;
    }
  }

  let epilogueStart = lines.length;
  for (let i = lines.length - 1; i >= preambleEnd; i--) {
    const trimmed = lines[i].trim();
    if (trimmed === "") continue;
    if (trimmed.startsWith(XML_TOKENS.CLOSE_TAG)) {
      epilogueStart = i;
      break;
    }
    break;
  }

  const preamble = lines.slice(0, preambleEnd).join("\n");
  const epilogue = lines.slice(epilogueStart).join("\n");
  const bodyLines = lines.slice(preambleEnd, epilogueStart);
  const blocks = new Map<string, ContentItem>();

  function registerBlock(tagName: string, rawLines: string[]): void {
    const innerLines = rawLines.slice(1, rawLines.length - 1);
    const childItems = parseXmlBlockContents(innerLines);
    const key = tagName.toLowerCase();

    const existing = blocks.get(key);
    if (existing) {
      // A block with this name was already registered (e.g. two top-level
      // <Person> blocks). Overwriting would silently discard the first
      // block's fields — merge instead so every field from every same-named
      // block stays discoverable, regardless of which tree node references it.
      existing.childItems = [...(existing.childItems ?? []), ...childItems];
    } else {
      const blockItem: ContentItem = {
        kind: "block",
        key,
        rawLines,
        openTag: rawLines[0],
        closeTag: rawLines[rawLines.length - 1],
        childItems,
      };
      blocks.set(key, blockItem);
      // Also register under the namespace-stripped bare key. XML tags carry a
      // structure prefix (<oneof:content_oneof>, <Map:resource_map>) but the tree
      // labels resolve to bare keys ("content_oneof", "resource_map"). Without this
      // alias the block lookup in renderXmlNode misses, which silently flattens a
      // oneof's members (dropping its <oneof:…> wrapper) and drops a map block
      // entirely. Don't clobber an existing bare key (a real top-level block of the
      // same bare name wins).
      const bareKey = stripXmlNamespacePrefix(key);
      if (bareKey && !blocks.has(bareKey)) {
        blocks.set(bareKey, blockItem);
      }
    }

    for (const item of childItems) {
      if (
        item.kind === "block" &&
        item.key &&
        item.rawLines &&
        item.rawLines.length > 1
      ) {
        registerBlock(item.key, item.rawLines);
      }
    }
  }
  const topLevelBlocks = parseXmlBodyBlocks(bodyLines);
  for (const block of topLevelBlocks)
    registerBlock(block.tagName, block.rawLines);

  return { blocks, preamble, epilogue };
}

function parseXmlBodyBlocks(
  lines: string[]
): Array<{ tagName: string; rawLines: string[] }> {
  const result: Array<{ tagName: string; rawLines: string[] }> = [];
  let currentBlockLines: string[] = [];
  let nestingDepth = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "" && nestingDepth === 0) continue;

    if (
      nestingDepth === 0 &&
      trimmed.startsWith(XML_TOKENS.OPEN_TAG) &&
      !trimmed.startsWith(XML_TOKENS.CLOSE_TAG)
    ) {
      if (isInlineLine(line)) {
        const tag = extractXmlTagName(trimmed);
        if (tag) result.push({ tagName: tag, rawLines: [line] });
        continue;
      }
      currentBlockLines = [line];
      nestingDepth = 1;
    } else if (nestingDepth > 0) {
      currentBlockLines.push(line);
      const opens = (line.match(/<(?![/?!])/g) || []).length;
      const closes = (line.match(/<\//g) || []).length;
      const selfClose = (line.match(/\/>/g) || []).length;
      nestingDepth += opens - selfClose - closes;
      if (nestingDepth <= 0) {
        nestingDepth = 0;
        const tag = extractXmlTagName(currentBlockLines[0].trim());
        if (tag) result.push({ tagName: tag, rawLines: currentBlockLines });
        currentBlockLines = [];
      }
    }
  }

  if (currentBlockLines.length > 0) {
    const tag = extractXmlTagName(currentBlockLines[0].trim());
    if (tag) result.push({ tagName: tag, rawLines: currentBlockLines });
  }

  return result;
}

function findXmlField(
  key: string,
  parsedDoc: XmlParsedDocument
): ContentItem | null {
  for (const block of parsedDoc.blocks.values()) {
    const found = searchItemInBlock(key, block.childItems ?? []);
    if (found) return found;
  }
  return null;
}

function searchItemInBlock(
  key: string,
  items: ContentItem[]
): ContentItem | null {
  for (const item of items) {
    if (item.kind === "field" && item.key === key) return item;
    if (item.kind === "block" && item.childItems) {
      const found = searchItemInBlock(key, item.childItems);
      if (found) return found;
    }
  }
  return null;
}

function renderXmlBlockAsIs(block: ContentItem, depth: number): string {
  const pad = INDENT.repeat(depth);
  const lines = block.rawLines;
  if (lines.length === 1) return pad + lines[0].trim();
  return [
    pad + lines[0].trim(),
    ...lines
      .slice(1, lines.length - 1)
      .map((line) => INDENT.repeat(depth + 1) + line.trim()),
    pad + lines[lines.length - 1].trim(),
  ].join("\n");
}

function renderXmlNode(
  node: HeTreeNode,
  parsedDoc: XmlParsedDocument,
  depth: number
): string | null {
  const label = node._original?.labelText ?? node.name ?? "";
  const keyInfo = labelToKeyInfo(label);
  if (!keyInfo) return null;

  const pad = INDENT.repeat(depth);

  if (keyInfo.kind === "field") {
    const item = findXmlField(keyInfo.key, parsedDoc);
    if (!item) return null;
    return (item.rawLines ?? []).map((line) => pad + line.trim()).join("\n");
  }

  const blockData = parsedDoc.blocks.get(keyInfo.key);
  const hasTreeChildren = node.children && node.children.length > 0;

  if (!hasTreeChildren) {
    if (!blockData) return null;
    return renderXmlBlockAsIs(blockData, depth);
  }

  const childDepth = blockData ? depth + 1 : depth;
  const renderedChildren: string[] = [];
  for (const child of node.children) {
    const rendered = renderXmlNode(child, parsedDoc, childDepth);
    if (rendered) renderedChildren.push(rendered);
  }

  if (blockData) {
    const openTag = pad + blockData.openTag.trim();
    const closeTag = pad + blockData.closeTag.trim();
    const opaqueLines: string[] = [];
    if (renderedChildren.length === 0) {
      for (const item of blockData.childItems ?? []) {
        if (item.kind === "opaque") {
          opaqueLines.push(
            ...(item.rawLines ?? []).map(
              (line) => INDENT.repeat(depth + 1) + line.trim()
            )
          );
        }
      }
    }
    return [openTag, ...opaqueLines, ...renderedChildren, closeTag].join("\n");
  } else {
    if (renderedChildren.length === 0) return null;
    return renderedChildren.join("\n");
  }
}

function reorderXmlContent(treeData: HeTreeNode[], content: string): string {
  const parsedDoc = buildXmlParsedDocument(content);
  const renderedTopLevelNodes: string[] = [];
  for (const node of treeData) {
    const rendered = renderXmlNode(node, parsedDoc, 1);
    if (rendered) renderedTopLevelNodes.push(rendered);
  }
  return [
    parsedDoc.preamble,
    ...renderedTopLevelNodes,
    parsedDoc.epilogue,
  ].join("\n");
}

// ═════════════════════════════════════════════════════════════════════════════
// Proto
// ═════════════════════════════════════════════════════════════════════════════

type ProtoItemKind = "field" | "block" | "opaque";

interface ProtoItem {
  kind: ProtoItemKind;
  key: string | null;
  rawLines: string[];
  childItems?: ProtoItem[];
}

interface ProtoParsedDocument {
  preamble: string[];
  blocks: Map<string, ProtoItem>;
  blockOrder: string[];
}

// ─── Proto parsing ────────────────────────────────────────────────────────────

function isProtoBlockOpen(trimmed: string): boolean {
  const PROTO_BLOCK_OPEN_PATTERN = new RegExp(
    `^(?:${PROTO_BLOCK_KEYWORDS.join("|")})\\s+\\S+\\s*\\{`
  );
  const PROTO_RPC_OPEN_PATTERN = new RegExp(
    `^(?:${PROTO_RPC_KEYWORDS.join("|")})\\s+\\S+\\s*\\(`
  );
  return (
    PROTO_BLOCK_OPEN_PATTERN.test(trimmed) ||
    PROTO_RPC_OPEN_PATTERN.test(trimmed)
  );
}

function extractProtoBlockKey(trimmed: string): string | null {
  const PROTO_BLOCK_KEY_PATTERN = new RegExp(
    `^(?:${PROTO_BLOCK_KEYWORDS.join("|")})\\s+(\\S+)`
  );
  const blockMatch = trimmed.match(PROTO_BLOCK_KEY_PATTERN);
  if (blockMatch) return blockMatch[1].toLowerCase();

  // RPC entries are written `rpc Name(Request) returns (Response);` with no
  // space between the name and `(`. Capture just the bare name (up to `(` or
  // whitespace) and prefix with `rpc:` so the key matches the tree label,
  // which produces "rpc:name" from "<Service rpc Name>".
  const PROTO_RPC_KEY_PATTERN = new RegExp(
    `^(?:${PROTO_RPC_KEYWORDS.join("|")})\\s+([^\\s(]+)`
  );
  const rpcMatch = trimmed.match(PROTO_RPC_KEY_PATTERN);
  if (rpcMatch) return `rpc:${rpcMatch[1]}`.toLowerCase();

  return null;
}

function extractProtoFieldKey(trimmed: string): string | null {
  const enumVal = trimmed.match(/^([A-Z_][A-Z0-9_]*)\s*=\s*\d+\s*;/);
  if (enumVal) return enumVal[1].toLowerCase();

  const mapField = trimmed.match(
    new RegExp(`^(?:${PROTO_MAP_KEYWORDS.join("|")})\\s*<[^>]+>\\s+(\\S+)\\s*=`)
  );
  if (mapField) return mapField[1].toLowerCase();

  const MODIFIER_PAT = PROTO_MODIFIERS.join("|");
  const PRIMITIVE_PAT = PROTO_PRIMITIVE_TYPES.join("|");

  const modField = trimmed.match(
    new RegExp(
      `^(?:${MODIFIER_PAT})\\s+(?:${PRIMITIVE_PAT}|[A-Za-z]\\S*)\\s+(\\S+)\\s*=`
    )
  );
  if (modField) return modField[1].toLowerCase();

  const primField = trimmed.match(
    new RegExp(`^(?:${PRIMITIVE_PAT})\\s+(\\S+)\\s*=`)
  );
  if (primField) return primField[1].toLowerCase();

  const msgField = trimmed.match(/^[A-Z]\S*\s+(\S+)\s*=/);
  if (msgField) return msgField[1].toLowerCase();

  return null;
}

function consumeProtoBlock(
  lines: string[],
  startIndex: number
): { rawLines: string[]; nextIndex: number } {
  const rawLines: string[] = [lines[startIndex]];
  let depth =
    (lines[startIndex].match(/\{/g) ?? []).length -
    (lines[startIndex].match(/\}/g) ?? []).length;
  let i = startIndex + 1;

  while (i < lines.length && depth > 0) {
    const line = lines[i];
    rawLines.push(line);
    depth +=
      (line.match(/\{/g) ?? []).length - (line.match(/\}/g) ?? []).length;
    i++;
  }

  return { rawLines, nextIndex: i };
}

function parseProtoBlockContents(lines: string[]): ProtoItem[] {
  const items: ProtoItem[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed || trimmed.startsWith("//")) {
      items.push({ kind: "opaque", key: null, rawLines: [line] });
      i++;
      continue;
    }

    if (trimmed === "}") {
      items.push({ kind: "opaque", key: null, rawLines: [line] });
      i++;
      continue;
    }

    if (isProtoBlockOpen(trimmed)) {
      const { rawLines, nextIndex } = consumeProtoBlock(lines, i);
      const key = extractProtoBlockKey(trimmed);
      const innerLines = rawLines.slice(1, rawLines.length - 1);
      const childItems = parseProtoBlockContents(innerLines);
      items.push({ kind: "block", key, rawLines, childItems });
      i = nextIndex;
      continue;
    }

    const fieldKey = extractProtoFieldKey(trimmed);
    if (fieldKey) {
      items.push({ kind: "field", key: fieldKey, rawLines: [line] });
      i++;
      continue;
    }

    items.push({ kind: "opaque", key: null, rawLines: [line] });
    i++;
  }

  return items;
}

function buildProtoParsedDocument(content: string): ProtoParsedDocument {
  const lines = content.split("\n");
  const preamble: string[] = [];
  const blocks = new Map<string, ProtoItem>();
  const blockOrder: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (isProtoBlockOpen(trimmed)) break;
    preamble.push(lines[i]);
    i++;
  }

  // Recursively register a block and all its nested child blocks.
  // Each nested block is stored under both its bare key AND a
  // parent-scoped key ("parentkey:childkey") so renderProtoNode can
  // find it even when the same name exists at multiple nesting levels.
  function registerNestedBlocks(parentKey: string, item: ProtoItem): void {
    for (const child of item.childItems ?? []) {
      if (child.kind === "block" && child.key) {
        const scopedKey = `${parentKey}:${child.key}`;
        blocks.set(scopedKey, child);
        // Also register under bare key if not already taken by a top-level block
        if (!blocks.has(child.key)) {
          blocks.set(child.key, child);
        }
        registerNestedBlocks(scopedKey, child);
      }
    }
  }

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      i++;
      continue;
    }

    if (isProtoBlockOpen(trimmed)) {
      const { rawLines, nextIndex } = consumeProtoBlock(lines, i);
      const key = extractProtoBlockKey(trimmed);
      if (key) {
        const innerLines = rawLines.slice(1, rawLines.length - 1);
        const childItems = parseProtoBlockContents(innerLines);
        const item: ProtoItem = { kind: "block", key, rawLines, childItems };
        blocks.set(key, item);
        blockOrder.push(key);
        registerNestedBlocks(key, item);
      }
      i = nextIndex;
      continue;
    }

    preamble.push(lines[i]);
    i++;
  }

  return { preamble, blocks, blockOrder };
}

// ─── Proto rendering ──────────────────────────────────────────────────────────

function renderProtoField(item: ProtoItem, depth: number): string {
  const pad = INDENT.repeat(depth);
  return item.rawLines.map((l) => pad + l.trim()).join("\n");
}

function renderProtoBlockAsIs(item: ProtoItem, depth: number): string {
  const pad = INDENT.repeat(depth);
  const lines = item.rawLines;
  if (lines.length === 1) return pad + lines[0].trim();
  return [
    pad + lines[0].trim(),
    ...lines
      .slice(1, lines.length - 1)
      .map((l) => INDENT.repeat(depth + 1) + l.trim()),
    pad + lines[lines.length - 1].trim(),
  ].join("\n");
}

function findProtoField(key: string, items: ProtoItem[]): ProtoItem | null {
  for (const item of items) {
    if (item.kind === "field" && item.key === key) return item;
    if (item.kind === "block" && item.childItems) {
      const found = findProtoField(key, item.childItems);
      if (found) return found;
    }
  }
  return null;
}

function renderProtoNode(
  node: HeTreeNode,
  parsedDoc: ProtoParsedDocument,
  depth: number,
  parentKey: string | null = null
): string | null {
  const label = node._original?.labelText ?? node.name ?? "";
  const keyInfo = labelToKeyInfo(label);
  if (!keyInfo) return null;

  const pad = INDENT.repeat(depth);

  if (keyInfo.kind === "field") {
    for (const block of parsedDoc.blocks.values()) {
      const item = findProtoField(keyInfo.key, block.childItems ?? []);
      if (item) return renderProtoField(item, depth);
    }
    return null;
  }

  // Try scoped key first ("parentkey:childkey"), then bare key.
  // This ensures nested blocks like Category inside Product are found
  // even when a bare "category" key doesn't exist at the top level.
  const scopedKey = parentKey ? `${parentKey}:${keyInfo.key}` : null;
  const blockData =
    (scopedKey ? parsedDoc.blocks.get(scopedKey) : null) ??
    parsedDoc.blocks.get(keyInfo.key) ??
    null;

  const hasTreeChildren = node.children && node.children.length > 0;

  if (!hasTreeChildren) {
    if (!blockData) return null;
    return renderProtoBlockAsIs(blockData, depth);
  }

  // Pass the resolved block's key downward so children can scope their lookups
  const resolvedKey = blockData?.key ?? keyInfo.key;
  const childDepth = blockData ? depth + 1 : depth;
  const renderedChildren: string[] = [];

  for (const child of node.children) {
    const rendered = renderProtoNode(child, parsedDoc, childDepth, resolvedKey);
    if (rendered) renderedChildren.push(rendered);
  }

  if (blockData) {
    const opaqueLines: string[] = [];
    for (const item of blockData.childItems ?? []) {
      if (item.kind === "opaque" && item.rawLines.some((l) => l.trim())) {
        opaqueLines.push(
          ...item.rawLines.map((l) => INDENT.repeat(depth + 1) + l.trim())
        );
      }
    }

    const openLine = pad + blockData.rawLines[0].trim();
    const closeLine =
      pad + blockData.rawLines[blockData.rawLines.length - 1].trim();

    const inner = [
      openLine,
      ...opaqueLines,
      ...renderedChildren,
      closeLine,
    ].join("\n");
    // Nested blocks (depth > 0) get a blank line before and after so they
    // are visually separated from adjacent fields inside the parent message.
    return depth > 0 ? inner + "\n" : inner;
  } else {
    if (renderedChildren.length === 0) return null;
    return renderedChildren.join("\n");
  }
}

function reorderProtoContent(treeData: HeTreeNode[], content: string): string {
  const parsedDoc = buildProtoParsedDocument(content);
  const renderedBlocks: string[] = [];

  for (const node of treeData) {
    const rendered = renderProtoNode(node, parsedDoc, 0, null);
    if (rendered) renderedBlocks.push(rendered);
  }

  // Safety net: append any blocks the tree didn't cover
  const renderedKeys = new Set(
    treeData
      .map((n) => {
        const label = n._original?.labelText ?? n.name ?? "";
        const ki = labelToKeyInfo(label);
        return ki?.kind === "block" ? ki.key : null;
      })
      .filter(Boolean) as string[]
  );

  for (const key of parsedDoc.blockOrder) {
    if (!renderedKeys.has(key)) {
      const blockToAppend = parsedDoc.blocks.get(key);
      if (blockToAppend != null && blockToAppend != undefined)
        renderedBlocks.push(renderProtoBlockAsIs(blockToAppend, 0));
    }
  }

  const preamble = parsedDoc.preamble.join("\n").replace(/\n+$/, "");
  // One blank line between preamble and first block, one between each block.
  return preamble + "\n\n" + renderedBlocks.join("\n\n") + "\n";
}
